import { useTenantedClient } from '../stores/org';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTenantedQueryKey } from './use-tenanted-query-key';
import { GetStackFramesForRunKeyResponse } from '../etc/api/typed-client';
import { fetchStackFramesForRunKey } from './stack-frame-fetch';

export { stackFrameRequestRateLimiter } from './stack-frame-fetch';

const DEFAULT_RECHECK_INTERVAL = 4000; // 4 seconds

const TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'cancelled',
  'stalled',
]);

// Helper function to check if any frame in the flat array is still running
const hasIncompleteFrames = (
  response: GetStackFramesForRunKeyResponse
): boolean => {
  return response.frames.some((frame) => !TERMINAL_STATUSES.has(frame.status));
};

export const useDataForRunKey = (params: {
  runKey: string;
  visible: boolean;
  fetchKey: string;
  refetchFrequency?: number;
}) => {
  const client = useTenantedClient();
  // Note: fetchKey intentionally excluded from cache key.
  // Cache is keyed only by runKey for stability across component remounts.
  // After re-running a cell, refetch() is called explicitly to update cached data.
  const tenantedKey = useTenantedQueryKey(['stack-frames', params.runKey]);

  return useSuspenseQuery({
    queryKey: tenantedKey,
    queryFn: async () => {
      return await fetchStackFramesForRunKey(client.atc, params.runKey);
    },
    retry: false,
    // Increase staleTime significantly to reduce unnecessary refetches
    // Data is considered fresh for 30 seconds, which prevents rapid refetching
    staleTime: 30_000,
    refetchInterval: (query) => {
      // Only poll if any frame in the tree is not completed
      // we poll on "error" in case the run gets retried
      const result = query.state.data;
      return result?.match(
        (response) => {
          if (!params.visible) {
            return false;
          }
          // response === null means "Not Run" — no need to poll
          const shouldPoll = response !== null && hasIncompleteFrames(response);
          if (!shouldPoll) {
            return false;
          }
          return params.refetchFrequency ?? DEFAULT_RECHECK_INTERVAL;
        },
        (_) => false
      );
    },
  });
};
